/**
 * Player settings — persisted to localStorage and applied to the audio engine + renderer.
 *
 * The quick MUTE toggle is available on the field mid-match; the FULL panel (volumes, haptics,
 * handedness, how-to-play, credits) lives on the start screen (see ui/settings.ts). This module is
 * just the store + (de)serialisation; wiring to music/feedback/renderer happens in main.ts.
 */
export type Handedness = 'right' | 'left';

export interface Settings {
  muted: boolean;
  musicVol: number; // 0..1
  sfxVol: number; // 0..1
  haptics: boolean;
  handedness: Handedness;
  /** First-run flag: the interactive tutorial auto-runs once until this is true. */
  tutorialDone: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  muted: false,
  musicVol: 0.7,
  sfxVol: 0.9,
  haptics: true,
  handedness: 'right',
  tutorialDone: false,
};

const KEY = 'fc-settings';
const LEGACY_MUTE_KEY = 'fc-muted'; // the original standalone mute flag

const clamp01 = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : fallback;

function sanitize(raw: unknown): Settings {
  const p = (raw ?? {}) as Partial<Record<keyof Settings, unknown>>;
  return {
    muted: typeof p.muted === 'boolean' ? p.muted : DEFAULT_SETTINGS.muted,
    musicVol: clamp01(p.musicVol, DEFAULT_SETTINGS.musicVol),
    sfxVol: clamp01(p.sfxVol, DEFAULT_SETTINGS.sfxVol),
    haptics: typeof p.haptics === 'boolean' ? p.haptics : DEFAULT_SETTINGS.haptics,
    handedness: p.handedness === 'left' ? 'left' : 'right',
    tutorialDone: typeof p.tutorialDone === 'boolean' ? p.tutorialDone : DEFAULT_SETTINGS.tutorialDone,
  };
}

/** Load settings (with defaults), migrating the legacy standalone mute flag on first run. */
export function loadSettings(): Settings {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_SETTINGS };
  try {
    const raw = localStorage.getItem(KEY);
    if (raw !== null) return sanitize(JSON.parse(raw));
    return { ...DEFAULT_SETTINGS, muted: localStorage.getItem(LEGACY_MUTE_KEY) === '1' };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: Settings): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* storage full / unavailable — ignore */
  }
}
